import { FC } from 'react';
import { BackgroundStyle } from '../../components/page/PageBackground';
import { PageContainer } from '../../components/page/PageContainer';
import { PageSizer, SizerMode } from '../../components/page/PageSizer';
import {
  isManagement,
  isStaff,
  Role,
  selectIsDelegatedManager,
  selectRequiresActingLocation,
  useAuthenticationService
} from '../../services/authentication/authentication-service';
import { ClockProvider } from '../../services/clockIn/ClockService';
import { useOptionalLocationService } from '../../services/location/LocationService';
import { TenantManagementPage } from '../tenantManagement/TenantManagementPage';
import { AdminTenantSettingsPage } from '../tenantSettings/AdminTenantSettingsPage';
import { AdminLocationGate } from './AdminLocationGate';
import { HomeContentsManagers } from './HomeContentsManagers';
import { HomeContentsStaff } from './HomeContentsStaff';
import { HomeHeader } from './HomeHeader';
import { HomeQuickLinks } from './HomeQuickLinks';

interface HomePageProps {}

export const HomePage: FC<HomePageProps> = () => {
  const [role, isDelegatedSuperAdmin, requiresActingLocation, isDelegatedManager] = useAuthenticationService(state => [
    state.role,
    state.isDelegatedSuperAdmin,
    selectRequiresActingLocation(state),
    selectIsDelegatedManager(state)
  ]);
  const selectedLocation = useOptionalLocationService(s => s.selectedLocation);
  const requiresAdminLocationSelection = requiresActingLocation && (!isDelegatedManager || !selectedLocation);

  if (role === Role.SuperAdmin) {
    return <TenantManagementPage />;
  }

  if (isDelegatedSuperAdmin) {
    return <AdminTenantSettingsPage />;
  }

  return (
    <ClockProvider>
      <PageContainer customHeader={<HomeHeader />} lockHeader backgroundStyle={BackgroundStyle.VIVID}>
        <PageSizer className="relative flex-col md:flex-row flex gap-5" mode={SizerMode.PAGE}>
          {requiresAdminLocationSelection && (
            <div className="md:hidden flex-1 overflow-x-hidden">
              <AdminLocationGate />
            </div>
          )}

          <div className="md:hidden w-full">
            <HomeQuickLinks />
          </div>
          {requiresAdminLocationSelection ? (
            <div className="hidden md:flex flex-1 overflow-x-hidden">
              <AdminLocationGate />
            </div>
          ) : (
            <>
              {isStaff(role) ? (
                <div className="flex-1 overflow-x-hidden">
                  <HomeContentsStaff />
                </div>
              ) : undefined}
              {isManagement(role) ? (
                <div className="flex-1 overflow-x-hidden">
                  <HomeContentsManagers />
                </div>
              ) : undefined}
            </>
          )}
          <div className="w-80 hidden md:block"></div>
        </PageSizer>
      </PageContainer>
    </ClockProvider>
  );
};
